import { useState } from "react";
import {
  Card, Table, Button, Tag, Modal, Form, Input, Switch, Popconfirm, message,
} from "antd";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  useCertificationList,
  useCertificationCreate,
  useCertificationUpdate,
  useCertificationDelete,
} from "../../../api-hooks/certifications";
import { TCertification } from "../../../types";
import type { ColumnsType } from "antd/es/table";

export default function Certifications() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TCertification | null>(null);
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();

  const { data: certs, isLoading } = useCertificationList();

  const { mutate: createCert, isPending: isCreating } = useCertificationCreate({
    onSuccess: () => {
      messageApi.success("Certification created.");
      setModalOpen(false);
      form.resetFields();
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to create certification."),
  });

  const { mutate: updateCert, isPending: isUpdating } = useCertificationUpdate({
    onSuccess: () => {
      messageApi.success("Certification updated.");
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to update certification."),
  });

  const { mutate: deleteCert } = useCertificationDelete({
    onSuccess: () => messageApi.success("Certification deleted."),
    onError: (err: any) => messageApi.error(err?.message || "Failed to delete certification."),
  });

  const openAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ is_required: false, is_active: true });
    setModalOpen(true);
  };

  const openEdit = (record: TCertification) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      category: record.category,
      description: record.description,
      is_required: record.is_required,
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (editingRecord) {
        updateCert({ id: editingRecord.id, ...values });
      } else {
        createCert(values);
      }
    });
  };

  const columns: ColumnsType<TCertification> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      render: (val: number) => <span className="text-xs text-slate-400">#{val}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (val: string) => <span className="text-sm font-medium text-slate-700">{val}</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (val: string | null) =>
        val ? (
          <Tag color="purple">{val}</Tag>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (val: string | null) => (
        <span className="text-xs text-slate-500">{val || "—"}</span>
      ),
    },
    {
      title: "Required",
      dataIndex: "is_required",
      key: "is_required",
      render: (val: boolean) =>
        val ? <Tag color="red">Required</Tag> : <Tag color="default">Optional</Tag>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (val: boolean) => <Tag color={val ? "green" : "default"}>{val ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleDateString("en-GB")}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TCertification) => (
        <div className="flex gap-2 justify-end">
          <Button
            size="small"
            icon={<Pencil size={12} />}
            style={{ color: "#4c1d95", borderColor: "#4c1d95" }}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this certification?"
            description="This action cannot be undone."
            onConfirm={() => deleteCert(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<Trash2 size={12} />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Certifications</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Manage certification and compliance definitions available across the platform
          </p>
        </div>
        <Button
          icon={<Plus size={14} />}
          style={{ background: "#4c1d95", borderColor: "#4c1d95", color: "#fff" }}
          onClick={openAdd}
        >
          Add Certification
        </Button>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        {/* No client-side pagination — full certification list loaded once */}
        <Table
          dataSource={certs ?? []}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          size="middle"
          scroll={{ x: 900 }}
          locale={{ emptyText: "No certifications defined yet." }}
        />
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        title={editingRecord ? `Edit Certification #${editingRecord.id}` : "Add Certification"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        okText={editingRecord ? "Save Changes" : "Create"}
        confirmLoading={isCreating || isUpdating}
        okButtonProps={{ style: { background: "#4c1d95", borderColor: "#4c1d95" } }}
      >
        <Form form={form} layout="vertical" className="mt-4" initialValues={{ is_required: false, is_active: true }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. CSCS Card, DBS Check" />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Input placeholder="e.g. Safety, Compliance, Driving" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Describe what this certification covers..." />
          </Form.Item>
          <div className="flex gap-6">
            <Form.Item name="is_required" label="Required" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
